import { zodResolver } from '@hookform/resolvers/zod';
import { Combobox } from '@optools/components/combobox/combobox';
import { MultiSelect } from '@optools/components/multi-select';
import { Button } from '@optools/components/ui/button';
import { DialogClose, DialogFooter } from '@optools/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@optools/components/ui/form';
import { Input } from '@optools/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@optools/components/ui/select';
import { PlaybackOverride } from '@optools/services/operator-gateway/overrides/playback/types';
import { ClientType } from '@optools/services/operator-gateway/overrides/types';
import {
  PlaybackExperience,
  PlaybackFeature,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

const schema = z.object({
  clientType: z.enum(ClientType),
  experience: z.enum(PlaybackExperience),
  features: z.enum(PlaybackFeature).array(),
  userId: z.string().min(1, { error: 'Okta ID is required' }),
});

export type PlaybackOverrideFormValues = z.infer<typeof schema>;

export interface PlaybackOverrideFormProps {
  loading: boolean;
  onSubmit: (values: PlaybackOverrideFormValues) => void;
  override?: PlaybackOverride | null;
}

const { ALL_CLIENTS, INTERNAL, ...restOfClientTypes } = ClientType;

const CLIENT_TYPE_OPTIONS = [ALL_CLIENTS, INTERNAL, ...Object.keys(restOfClientTypes)].map(
  (value) => ({
    label: value,
    value,
  }),
);

const FEATURE_OPTIONS = Object.keys(PlaybackFeature).map((value) => ({
  label: value,
  value,
}));

export function PlaybackOverrideForm({ loading, onSubmit, override }: PlaybackOverrideFormProps) {
  const {
    clientType = ALL_CLIENTS,
    experience = PlaybackExperience.FASTLY,
    features = [],
    userId = '',
  } = override || {};

  const form = useForm<PlaybackOverrideFormValues>({
    defaultValues: { clientType, experience, features, userId },
    disabled: loading,
    resolver: zodResolver(schema),
  });

  return (
    <Form {...form}>
      <form aria-label="override" className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="userId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Okta ID</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="clientType"
          render={({ field }) => {
            const { onChange, ...rest } = field;
            return (
              <FormItem>
                <FormLabel>Client Type</FormLabel>
                <FormControl>
                  <Combobox data={CLIENT_TYPE_OPTIONS} onValueChange={onChange} {...rest} />
                </FormControl>
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="experience"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>Experience</FormLabel>
                <Select
                  defaultValue={field.value}
                  disabled={field.disabled}
                  onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.keys(PlaybackExperience).map((value) => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="features"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>Features</FormLabel>
                <FormControl>
                  <MultiSelect
                    disabled={field.disabled}
                    options={FEATURE_OPTIONS}
                    placeholder="Select features..."
                    selected={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            );
          }}
        />

        <DialogFooter>
          <DialogClose asChild>
            <Button disabled={loading} variant="secondary">
              Cancel
            </Button>
          </DialogClose>
          <Button disabled={loading} loading={loading} type="submit">
            Save
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
